import { CheckCircle2, CloudUpload, Download, FileJson2, Image, LoaderCircle, Save, Trash2, UploadCloud, XCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { strToU8, zipSync } from "fflate";
import { SpritePlayer } from "codexpetlab-player";
import { validateLocalPetFiles, type LocalPetValidation } from "../lib/local-pet-validation";
import { deleteLocalPet, listLocalPets, saveLocalPet, type LocalPetRecord } from "../lib/local-pets";

const AVAILABLE_TAGS = ["animal", "person", "robot", "fantasy", "cute", "funny", "anime", "game", "creature", "focused", "AI", "meme"];

function copyBytes(bytes: Uint8Array): ArrayBuffer {
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(bytes);
  return copy.buffer;
}

function makeZip(manifestBytes: Uint8Array, spritesheetBytes: Uint8Array): Uint8Array {
  return zipSync({ "pet.json": manifestBytes, "spritesheet.webp": spritesheetBytes }, { level: 6 });
}

function downloadBytes(bytes: Uint8Array, fileName: string) {
  const url = URL.createObjectURL(new Blob([copyBytes(bytes)], { type: "application/zip" }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function LocalPetCard({ pet, onDelete }: { pet: LocalPetRecord; onDelete: (id: string) => void }) {
  const { t } = useTranslation();
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    const next = URL.createObjectURL(pet.spritesheet);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [pet.spritesheet]);

  const download = async () => {
    const manifestBytes = strToU8(`${JSON.stringify(pet.manifest, null, 2)}\n`);
    const spritesheetBytes = new Uint8Array(await pet.spritesheet.arrayBuffer());
    downloadBytes(makeZip(manifestBytes, spritesheetBytes), `${pet.id}.codex-pet.zip`);
  };

  return (
    <article className="local-pet-card">
      <div className="local-pet-preview">
        {url && <SpritePlayer src={url} state="idle" width={150} aria-label={`${pet.manifest.displayName} idle animation`} />}
      </div>
      <div className="local-pet-info">
        <span className="filter-label">{t("gallery.directions")} · {t("nav.library")}</span>
        <h3>{pet.manifest.displayName}</h3>
        <p>{pet.manifest.description}</p>
        <div className="tag-list">{pet.tags.map((tag) => <span key={tag}>{tag}</span>)}</div>
      </div>
      <div className="local-pet-actions">
        <button type="button" onClick={() => void download()}><Download size={15} /> {t("gallery.download")}</button>
        <button type="button" onClick={() => onDelete(pet.id)}><Trash2 size={15} /> {t("library.remove")}</button>
      </div>
    </article>
  );
}

export function UploadPage() {
  const { t } = useTranslation();
  const [manifestFile, setManifestFile] = useState<File | null>(null);
  const [spritesheetFile, setSpritesheetFile] = useState<File | null>(null);
  const [manifestBytes, setManifestBytes] = useState<Uint8Array | null>(null);
  const [spritesheetBytes, setSpritesheetBytes] = useState<Uint8Array | null>(null);
  const [validation, setValidation] = useState<LocalPetValidation | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [tags, setTags] = useState<string[]>([]);
  const [creatorName, setCreatorName] = useState("");
  const [localPets, setLocalPets] = useState<LocalPetRecord[]>([]);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [cloudSubmission, setCloudSubmission] = useState<string | null>(null);

  const refreshLibrary = async () => setLocalPets(await listLocalPets());

  useEffect(() => { void refreshLibrary(); }, []);
  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  useEffect(() => {
    let active = true;
    const validate = async () => {
      setValidation(null);
      setMessage(null);
      setCloudSubmission(null);
      if (!manifestFile || !spritesheetFile) return;
      setBusy(true);
      try {
        const nextManifestBytes = new Uint8Array(await manifestFile.arrayBuffer());
        const nextSpritesheetBytes = new Uint8Array(await spritesheetFile.arrayBuffer());
        if (!active) return;
        setManifestBytes(nextManifestBytes);
        setSpritesheetBytes(nextSpritesheetBytes);
        setValidation(validateLocalPetFiles(nextManifestBytes, nextSpritesheetBytes));
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setPreviewUrl(URL.createObjectURL(spritesheetFile));
      } finally {
        if (active) setBusy(false);
      }
    };
    void validate();
    return () => { active = false; };
  }, [manifestFile, spritesheetFile]);

  const toggleTag = (tag: string) => {
    setTags((current) => current.includes(tag) ? current.filter((item) => item !== tag) : current.length < 3 ? [...current, tag] : current);
  };

  const save = async () => {
    if (!validation?.ok || !validation.manifest || !spritesheetFile) return;
    setBusy(true);
    try {
      await saveLocalPet({
        id: validation.manifest.id,
        manifest: validation.manifest,
        spritesheet: spritesheetFile,
        tags,
      });
      await refreshLibrary();
      setMessage(`${validation.manifest.displayName} saved to this browser.`);
    } finally {
      setBusy(false);
    }
  };

  const submitCloud = async () => {
    if (!validation?.ok || !validation.manifest || !manifestBytes || !spritesheetBytes) return;
    setBusy(true);
    setMessage(null);
    try {
      const zip = makeZip(manifestBytes, spritesheetBytes);
      const formData = new FormData();
      formData.set("file", new File([copyBytes(zip)], `${validation.manifest.id}.codex-pet.zip`, { type: "application/zip" }));
      formData.set("creatorName", creatorName);
      formData.set("tags", JSON.stringify(tags));
      const response = await fetch("/api/submissions", { method: "POST", body: formData });
      const body = await response.json() as { error?: string; submission?: { id: string; status: string } };
      if (!response.ok || !body.submission) throw new Error(body.error || `Submission failed with status ${response.status}`);
      setCloudSubmission(`${body.submission.status} · ${body.submission.id}`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Submission failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: string) => {
    await deleteLocalPet(id);
    await refreshLibrary();
  };

  return (
    <section className="creator-studio">
      <div className="studio-heading">
        <div><p className="eyebrow">{t("library.eyebrow")}</p><h2>{t("library.title")}</h2><p>{t("library.description")}</p></div>
        <div className="studio-local-count"><strong>{localPets.length}</strong><span>{t("library.localPets")}</span></div>
      </div>

      <div className="studio-layout">
        <div className="studio-upload-column" id="upload-form">
          <div className="studio-preview">
            {previewUrl && validation?.ok ? <SpritePlayer src={previewUrl} state="idle" width={300} aria-label="Local pet idle preview" /> : <><UploadCloud size={34} /><strong>{t("library.uploadHint")}</strong></>}
          </div>

          <label className="studio-file-field">
            <span><FileJson2 size={16} /> pet.json</span>
            <input type="file" accept="application/json,.json" onChange={(event) => setManifestFile(event.target.files?.[0] ?? null)} />
            <strong>{manifestFile?.name ?? "Choose file"}</strong>
          </label>

          <label className="studio-file-field">
            <span><Image size={16} /> spritesheet.webp</span>
            <input type="file" accept="image/webp,.webp" onChange={(event) => setSpritesheetFile(event.target.files?.[0] ?? null)} />
            <strong>{spritesheetFile?.name ?? "Choose file"}</strong>
          </label>

          <div className="studio-tags">
            <span className="filter-label">{t("library.tags")}</span>
            <div className="chip-row">{AVAILABLE_TAGS.map((tag) => <button className={tags.includes(tag) ? "chip selected" : "chip"} type="button" key={tag} onClick={() => toggleTag(tag)} disabled={!tags.includes(tag) && tags.length >= 3}>{tag}</button>)}</div>
          </div>

          <label className="creator-field"><span>{t("library.creator")}</span><input value={creatorName} onChange={(event) => setCreatorName(event.target.value)} maxLength={80} placeholder={t("library.optional")} /></label>

          {busy && <div className="validation-loading"><LoaderCircle className="spin" size={18} /> {t("library.processing")}</div>}
          {validation && <div className={validation.ok ? "validation-report passed" : "validation-report failed"}><div className="validation-title">{validation.ok ? <CheckCircle2 size={20} /> : <XCircle size={20} />}<strong>{validation.ok ? t("library.validationPassed") : t("library.validationFailed")}</strong></div>{validation.manifest && <p><code>{validation.manifest.id}</code> · {t("gallery.directions")} · {validation.dimensions?.width}×{validation.dimensions?.height}</p>}<ul>{validation.issues.map((issue) => <li className={issue.severity} key={`${issue.code}-${issue.message}`}><span><strong>{issue.code}</strong> {issue.message}</span></li>)}</ul></div>}
          {message && <div className="upload-error">{message}</div>}
          {cloudSubmission && <div className="submission-success"><CloudUpload size={18} /><div><strong>Submitted for review</strong><p>{cloudSubmission}</p></div></div>}

          <div className="studio-actions">
            <button className="primary-button" type="button" disabled={!validation?.ok || busy} onClick={() => void save()}><Save size={17} /> {t("library.save")}</button>
            <button className="secondary-button" type="button" disabled={!validation?.ok || busy} onClick={() => void submitCloud()}><CloudUpload size={17} /> {t("library.submit")}</button>
          </div>
        </div>

        <div className="studio-library-column" id="local-library">
          <div className="studio-library-heading"><div><span className="eyebrow">{t("library.eyebrow")}</span><h3>{t("library.title")}</h3></div><span>{localPets.length}</span></div>
          {localPets.length === 0 ? <div className="studio-empty-library"><UploadCloud size={28} /><strong>{t("library.noPets")}</strong><p>{t("library.noPetsHint")}</p></div> : <div className="local-pet-list">{localPets.map((pet) => <LocalPetCard key={pet.id} pet={pet} onDelete={(id) => void remove(id)} />)}</div>}
        </div>
      </div>
    </section>
  );
}
